/**
 * Shared page body for the two Policy-group checklists — Endorsements
 * ("Policy Forms & Endorsements") and Subjectivities. Pure CRUD, no
 * calculations: grouped checkbox rows with per-row delete, an inline
 * add-row per group, and Save & Continue persistence through the
 * `policy-checklists` swap boundary (mock until the backend ships
 * alongside the Loss Runs changes).
 *
 * Domain rules (jun-11 sync): defaults come from the server and are
 * toggleable/deletable; custom rows are REQUIRED — born checked, the
 * checkbox is locked, removing the row is the only undo.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Calendar, SearchLg, Trash02 } from "@untitledui/icons";
import { Checkbox, Input } from "@tricura/atlas-ui";
import type { ChecklistGroup, ChecklistKind } from "../../../api";
import { listChecklist, saveChecklist } from "../../../api";
import { Skeleton } from "../../../components/skeleton";
import { SubmissionFooter } from "../../../components/submission-footer";
import { useSubmissionContext } from "../context";
import { getNextSection } from "../sections";

interface ChecklistPageProps {
    sectionId: string;
    kind: ChecklistKind;
    title: string;
    subtitle: string;
    /** Endorsements has a catalog search above the groups. */
    withSearch?: boolean;
    /** Subjectivities renders select-all as its own row under the header
     *  (per Figma); Endorsements keeps it inline on each group header. */
    selectAllAsRow?: boolean;
}

export function ChecklistPage({ sectionId, kind, title, subtitle, withSearch, selectAllAsRow }: ChecklistPageProps) {
    const { detail } = useSubmissionContext();
    const navigate = useNavigate();

    const [groups, setGroups] = useState<ChecklistGroup[] | null>(null);
    /** Serialized last-saved groups — drives the footer's dirty flag. */
    const [baselineSig, setBaselineSig] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    /** Group id with its add-row open, the draft label, and whether the new
     *  item starts checked (user-toggleable during creation). */
    const [draft, setDraft] = useState<{ groupId: string; label: string; checked: boolean } | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setGroups(null);
        void listChecklist(detail.id, kind).then((loaded) => {
            if (cancelled) return;
            setGroups(loaded);
            setBaselineSig(JSON.stringify(loaded));
        });
        return () => {
            cancelled = true;
        };
    }, [detail.id, kind]);

    const updateGroup = useCallback((groupId: string, update: (group: ChecklistGroup) => ChecklistGroup) => {
        setGroups((prev) => (prev ? prev.map((g) => (g.id === groupId ? update(g) : g)) : prev));
    }, []);

    const toggleItem = useCallback(
        (groupId: string, itemId: string, next: boolean) => {
            updateGroup(groupId, (g) => ({
                ...g,
                items: g.items.map((i) => (i.id === itemId && !i.custom ? { ...i, checked: next } : i)),
            }));
        },
        [updateGroup],
    );

    /** Header select-all: customs are required-checked already, so it
     *  only flips the server defaults. */
    const toggleAll = useCallback(
        (groupId: string, next: boolean) => {
            updateGroup(groupId, (g) => ({
                ...g,
                items: g.items.map((i) => (i.custom ? i : { ...i, checked: next })),
            }));
        },
        [updateGroup],
    );

    const deleteItem = useCallback(
        (groupId: string, itemId: string) => {
            updateGroup(groupId, (g) => ({ ...g, items: g.items.filter((i) => i.id !== itemId) }));
        },
        [updateGroup],
    );

    const confirmDraft = useCallback(() => {
        if (!draft) return;
        const label = draft.label.trim();
        if (label === "") {
            setDraft(null);
            return;
        }
        updateGroup(draft.groupId, (g) => ({
            ...g,
            // Custom row keeps the checked state the user chose while creating it.
            // Stamp the group's EndorsementType (endorsements only) so the row
            // persists its category and re-buckets correctly on reload.
            items: [{ id: `custom-${crypto.randomUUID()}`, label, checked: draft.checked, custom: true, type: g.type }, ...g.items],
        }));
        setDraft(null);
    }, [draft, updateGroup]);

    const next = getNextSection(sectionId);

    // Save persists the checklist without navigating; Continue advances
    // (the footer guards against leaving while dirty).
    const onSave = useCallback(async () => {
        if (!groups) return;
        setIsSaving(true);
        try {
            // Adopt the re-fetched server state (fresh ids + refreshed diff
            // baseline) so the next toggle→Save cycle diffs correctly (OR-1304).
            const fresh = await saveChecklist(detail.id, kind, groups);
            setGroups(fresh);
            setBaselineSig(JSON.stringify(fresh));
        } finally {
            setIsSaving(false);
        }
    }, [groups, detail.id, kind]);
    const onContinue = () => navigate(next ? `../${next.path}` : "/submissions");
    const isDirty = groups !== null && baselineSig !== null && JSON.stringify(groups) !== baselineSig;

    const needle = search.trim().toLowerCase();
    const visibleGroups = useMemo(() => {
        if (!groups) return null;
        if (needle === "") return groups;
        return groups.map((g) => ({
            ...g,
            items: g.items.filter((i) => i.label.toLowerCase().includes(needle)),
        }));
    }, [groups, needle]);

    const renewal = detail.accountBasics.renewalDate || "";
    const expiration = detail.accountBasics.expirationDate || "";

    return (
        <>
            <div className="w-full space-y-6">
                <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-primary text-md font-semibold">{title}</h2>
                        <p className="text-tertiary text-sm">{subtitle}</p>
                    </div>
                    <div className="flex w-full items-stretch justify-between gap-6 md:w-auto md:shrink-0 md:justify-start">
                        <DateField label="Renewal Date" value={renewal} />
                        <span aria-hidden="true" className="bg-border-secondary hidden w-px self-stretch md:block" />
                        <DateField label="Expiration Date" value={expiration} />
                    </div>
                </header>

                {/* One sequential list for every category (June 4th
                 * meeting request) — a single card where each group is
                 * an inline gray header row with its own Add Row. */}
                <section className="border-secondary bg-primary shadow-xs overflow-hidden rounded-xl border">
                    {withSearch && (
                        <div className="flex justify-end px-4 py-3 sm:px-6">
                            <div className="w-full md:w-72">
                                <Input
                                    size="sm"
                                    icon={SearchLg}
                                    data-autotest={`${kind}-search-input`}
                                    placeholder="Search"
                                    value={search}
                                    onChange={setSearch}
                                    aria-label={`Search ${kind}`}
                                />
                            </div>
                        </div>
                    )}

                    {visibleGroups === null ? (
                        <ChecklistSkeleton />
                    ) : (
                        visibleGroups.map((group) => (
                            <div key={group.id} className="border-secondary border-t first:border-t-0">
                                <div
                                    data-autotest={`${kind}-checklist-group-header`}
                                    className={`${selectAllAsRow ? "bg-primary" : "bg-secondary"} border-secondary min-h-14 flex border-b`}
                                >
                                    <div className="flex flex-1 items-center justify-between gap-3 px-4 sm:px-6">
                                        {/* Select-all is inline on the header for Endorsements
                                         * (Linda's jun-12 update); Subjectivities renders it as
                                         * its own row below (see `selectAllAsRow`). */}
                                        <div className="flex items-center gap-3">
                                            {!selectAllAsRow && (
                                                <Checkbox
                                                    size="sm"
                                                    aria-label={`Select all ${group.title}`}
                                                    isSelected={group.items.length > 0 && group.items.every((i) => i.checked)}
                                                    isIndeterminate={
                                                        group.items.some((i) => i.checked) && !group.items.every((i) => i.checked)
                                                    }
                                                    isDisabled={group.items.length === 0}
                                                    onChange={(next) => toggleAll(group.id, next)}
                                                    data-autotest={`${kind}-checklist-select-all-checkbox`}
                                                />
                                            )}
                                            <h3 className="text-secondary text-xs font-semibold sm:text-sm">{group.title}</h3>
                                        </div>
                                        <button
                                            type="button"
                                            data-autotest={`${group.id}-checklist-add-row-button`}
                                            className="text-primary hover:text-brand-secondary shrink-0 text-sm font-semibold transition"
                                            onClick={() => setDraft({ groupId: group.id, label: "", checked: true })}
                                        >
                                            + Add Row
                                        </button>
                                    </div>
                                    {/* Action column — keeps the vertical divider continuous. */}
                                    <div className="border-secondary w-16 shrink-0 border-l" aria-hidden="true" />
                                </div>
                                <ul className="divide-secondary divide-y">
                                    {selectAllAsRow && group.items.length > 0 && (
                                        <li className="bg-secondary min-h-11 flex">
                                            <div className="flex flex-1 items-center px-4 sm:px-6">
                                                <Checkbox
                                                    size="sm"
                                                    aria-label={`Select all ${group.title}`}
                                                    isSelected={group.items.every((i) => i.checked)}
                                                    isIndeterminate={
                                                        group.items.some((i) => i.checked) && !group.items.every((i) => i.checked)
                                                    }
                                                    onChange={(next) => toggleAll(group.id, next)}
                                                    data-autotest={`${kind}-checklist-select-all-checkbox`}
                                                />
                                            </div>
                                            <div className="border-secondary w-16 shrink-0 border-l" aria-hidden="true" />
                                        </li>
                                    )}
                                    {draft?.groupId === group.id && (
                                        <li
                                            className="min-h-16 flex"
                                            // Confirm only when focus leaves the whole draft row —
                                            // not when it moves to the row's own checkbox/trash.
                                            // Lets the user toggle the checkbox without the input's
                                            // blur prematurely creating/discarding the item.
                                            onBlur={(e) => {
                                                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) confirmDraft();
                                            }}
                                        >
                                            <div className="flex flex-1 items-center gap-3 px-4 sm:px-6">
                                                <Checkbox
                                                    size="sm"
                                                    aria-label="Mark new item as completed"
                                                    isSelected={draft.checked}
                                                    onChange={(next) => setDraft({ ...draft, checked: next })}
                                                />
                                                <Input
                                                    size="sm"
                                                    placeholder={group.placeholder ?? "Enter item"}
                                                    value={draft.label}
                                                    onChange={(label) => setDraft({ ...draft, label })}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") confirmDraft();
                                                        if (e.key === "Escape") setDraft(null);
                                                    }}
                                                    aria-label={`New ${group.title} item`}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="border-secondary flex w-16 shrink-0 items-center justify-center border-l">
                                                <button
                                                    type="button"
                                                    aria-label="Discard new item"
                                                    className="text-fg-quaternary hover:text-fg-error-primary rounded p-1 transition"
                                                    // onMouseDown so it wins over the input's confirm-on-blur.
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setDraft(null);
                                                    }}
                                                >
                                                    <Trash02 aria-hidden="true" className="size-4" />
                                                </button>
                                            </div>
                                        </li>
                                    )}
                                    {group.items.map((item) => (
                                        <li key={item.id} className="min-h-16 flex">
                                            <div className="flex flex-1 items-center px-4 sm:px-6">
                                                <Checkbox
                                                    size="sm"
                                                    label={item.label}
                                                    hint={item.description}
                                                    isSelected={item.checked}
                                                    isDisabled={item.custom}
                                                    onChange={(next) => toggleItem(group.id, item.id, next)}
                                                    data-autotest={`${kind}-checklist-item-checkbox`}
                                                />
                                            </div>
                                            <div className="border-secondary flex w-16 shrink-0 items-center justify-center border-l">
                                                <button
                                                    type="button"
                                                    aria-label={`Delete ${item.label}`}
                                                    data-autotest={`${kind}-checklist-item-delete-button`}
                                                    className="text-fg-quaternary hover:text-fg-error-primary rounded p-1 transition"
                                                    onClick={() => deleteItem(group.id, item.id)}
                                                >
                                                    <Trash02 aria-hidden="true" className="size-4" />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                    {group.items.length === 0 && draft?.groupId !== group.id && (
                                        <li className="text-tertiary px-4 py-4 text-sm sm:px-6">
                                            {needle === "" ? "No items — use Add Row to create one." : "No items match the search."}
                                        </li>
                                    )}
                                </ul>
                            </div>
                        ))
                    )}
                </section>
            </div>

            <SubmissionFooter
                onSave={onSave}
                onContinue={onContinue}
                isDirty={isDirty}
                isSaving={isSaving}
                nextSectionLabel={next?.label ?? null}
            />
        </>
    );
}

function ChecklistSkeleton() {
    return (
        <div className="space-y-3 p-4 sm:p-6">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
        </div>
    );
}

function DateField({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-tertiary text-xs font-normal">{label}</span>
            <span className="text-secondary inline-flex items-center gap-1.5 text-xs font-semibold">
                <Calendar aria-hidden="true" className="text-fg-quaternary size-4" />
                {value || <span className="text-placeholder font-normal">—</span>}
            </span>
        </div>
    );
}
